"use client";

import { Badge } from "@/components/ui/badge";
import { useGetCampaignAuditHistoryQuery } from "@/services/campaign";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  History,
  Loader2,
  Pencil,
  PlusCircle,
  Store,
  User,
} from "lucide-react";
import { useState } from "react";
import { safeFormatDate } from "../utils/date-utils";

interface CampaignAuditHistoryContentProps {
  type: "PRO013" | "PRO014" | "PRO017";
  code: number;
}

/**
 * Shape do AuditLog do Prisma (snake_case). Backend retorna direto via
 * findMany, sem transform — todos os fields seguem o schema:
 *   - event_type: enum (ACTION_EXECUTED, STATUS_CHANGED, ITEM_ADDED, ...)
 *   - event_payload: JSON livre com {domain, action, target, metadata, changes, message}
 *   - email: actor email
 *   - created_at: ISO timestamp
 */
interface AuditEvent {
  id: number | string;
  entity_type?: string;
  entity_id?: string;
  event_type?: string;
  event_payload?: Record<string, any> | null;
  action?: string;
  email?: string | null;
  ip?: string | null;
  created_at?: string | null;
  // Campos camelCase mantidos como fallback caso algum endpoint diferente
  // venha a expor o response transformado.
  eventType?: string;
  createdAt?: string | null;
  actorEmail?: string | null;
  data?: Record<string, any>;
}

/**
 * Mapa de cor + ícone + label legível por action.
 * Uso de Record evita switch/else-if (regra do projeto — MEMORY.md §Code style).
 */
const ACTION_META: Record<
  string,
  { label: string; icon: typeof PlusCircle; color: string; bg: string }
> = {
  CAMPAIGN_CREATED: {
    label: "Campanha criada",
    icon: PlusCircle,
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
  },
  CAMPAIGN_UPDATED: {
    label: "Campanha atualizada",
    icon: Pencil,
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
  },
  EXTEND_END_DATE: {
    label: "Prorrogação",
    icon: Clock,
    color: "text-purple-700",
    bg: "bg-purple-50 border-purple-200",
  },
  CAMPAIGN_STORES_UPDATED: {
    label: "Lojas atualizadas",
    icon: Store,
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
  },
};

const FIELD_LABEL: Record<string, string> = {
  description: "Descrição",
  startDate: "Data de início",
  endDate: "Data de término",
  takeQuantity: "Leve",
  payQuantity: "Pague",
  discountValue: "Valor do desconto",
  discountType: "Tipo de desconto",
  paymentMethod: "Forma de pagamento",
  baseValue: "Valor base mínimo",
  allStoreFlag: "Todas as lojas",
  storeCode: "Loja",
  stores: "Lojas",
  storesAdded: "Lojas adicionadas",
  storesRemoved: "Lojas removidas",
  departments: "Departamentos",
  groups: "Grupos",
  itemsCount: "Quantidade de itens",
  itemsTotal: "Quantidade total de itens",
  listsCount: "Quantidade de listas",
  from: "De",
  to: "Para",
};

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.length === 0 ? "(vazio)" : v.join(", ");
  if (v instanceof Date) return safeFormatDate(v, "dd/MM/yyyy", String(v));
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return safeFormatDate(v, "dd/MM/yyyy", v);
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const fieldLabel = (key: string): string => FIELD_LABEL[key] ?? key;

/**
 * Helpers de leitura tolerantes — Prisma retorna snake_case mas alguns
 * endpoints podem expor camelCase transformado. Aceita ambos.
 */
const eventTimestamp = (event: AuditEvent): unknown =>
  event.created_at ?? event.createdAt ?? null;

const eventId = (event: AuditEvent): string =>
  String(event.id ?? Math.random());

const eventPayload = (event: AuditEvent): Record<string, any> =>
  (event.event_payload ?? event.data ?? {}) as Record<string, any>;

const eventActorEmail = (event: AuditEvent): string | null =>
  event.email ?? event.actorEmail ?? null;

/**
 * Action: o action específico (ex: CAMPAIGN_UPDATED) vive dentro do
 * event_payload. event_type do prisma é o enum genérico (ACTION_EXECUTED,
 * STATUS_CHANGED, etc). Tentamos ambos com fallback.
 */
const eventAction = (event: AuditEvent): string => {
  const payload = eventPayload(event);
  return (
    payload.action ??
    event.action ??
    event.event_type ??
    event.eventType ??
    "UNKNOWN"
  );
};

export function CampaignAuditHistoryContent({
  type,
  code,
}: CampaignAuditHistoryContentProps) {
  const { data, isLoading, error } = useGetCampaignAuditHistoryQuery({ type, code });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-destructive font-medium">
          Erro ao carregar histórico
        </p>
        <p className="text-xs text-muted-foreground">
          Tente novamente em instantes.
        </p>
      </div>
    );
  }

  const events: AuditEvent[] = data?.events ?? [];
  // Sort desc — usa helper tolerante a snake/camel + datas inválidas (NaN
  // colapsa pra 0 via `|| 0` mantendo ordem estável).
  const tsAsNumber = (e: AuditEvent): number => {
    const ts = eventTimestamp(e);
    if (!ts) return 0;
    const t = new Date(ts as string | number).getTime();
    return isNaN(t) ? 0 : t;
  };
  const sortedEvents = [...events].sort((a, b) => tsAsNumber(b) - tsAsNumber(a));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b">
        <History className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {type} #{code}
        </span>
        <Badge variant="secondary" className="ml-auto">
          {sortedEvents.length} evento(s)
        </Badge>
      </div>

      {sortedEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
          <History className="h-8 w-8 opacity-40" />
          <p className="text-sm">Sem eventos registrados pra esta campanha</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedEvents.map((event) => {
            const action = eventAction(event);
            const meta = ACTION_META[action] ?? {
              label: action,
              icon: CheckCircle2,
              color: "text-gray-700",
              bg: "bg-gray-50 border-gray-200",
            };
            const Icon = meta.icon;
            const id = eventId(event);
            const ts = eventTimestamp(event);
            const actorEmail = eventActorEmail(event);
            const payload = eventPayload(event);
            const isOpen = expanded.has(id);
            const hasDetails =
              !!payload.changes || !!payload.target || !!payload.metadata;
            const changes = (payload.changes ?? []) as Array<{
              field: string;
              from?: unknown;
              to?: unknown;
            }>;

            return (
              <div
                key={id}
                className={`rounded-lg border ${meta.bg} overflow-hidden`}
              >
                <button
                  type="button"
                  onClick={() => hasDetails && toggle(id)}
                  className={`w-full text-left p-3 flex items-start gap-3 ${
                    hasDetails ? "hover:opacity-80 cursor-pointer" : "cursor-default"
                  }`}
                  disabled={!hasDetails}
                >
                  <div className={`flex-shrink-0 mt-0.5 ${meta.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${meta.color}`}>
                        {meta.label}
                      </span>
                      {payload.message && (
                        <span className="text-xs text-muted-foreground">
                          — {payload.message}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {safeFormatDate(ts, "dd/MM/yyyy 'às' HH:mm")}
                      </div>
                      {actorEmail && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {actorEmail}
                        </div>
                      )}
                      {changes.length > 0 && (
                        <Badge variant="outline" className="text-[10px] py-0 h-4">
                          {changes.length} campo(s)
                        </Badge>
                      )}
                    </div>
                  </div>
                  {hasDetails && (
                    <div className="flex-shrink-0 mt-0.5 text-muted-foreground">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </button>

                {isOpen && hasDetails && (
                  <div className="px-3 pb-3 pt-0 ml-7 space-y-2 border-t border-current/10 pt-3 mt-1">
                    {changes.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-muted-foreground">
                          Alterações
                        </p>
                        <div className="space-y-1">
                          {changes.map((c, idx) => (
                            <div
                              key={idx}
                              className="text-xs grid grid-cols-[140px_1fr] gap-2 py-1 border-b border-current/5 last:border-0"
                            >
                              <span className="font-medium text-foreground/80 truncate">
                                {fieldLabel(c.field)}
                              </span>
                              <span className="font-mono text-foreground/70 break-all">
                                <span className="text-muted-foreground line-through mr-1">
                                  {formatValue(c.from)}
                                </span>
                                <span>→</span>
                                <span className="ml-1 font-medium text-foreground">
                                  {formatValue(c.to)}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {payload.target && Object.keys(payload.target).length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-muted-foreground">
                          {action === "EXTEND_END_DATE" ? "Resumo" : "Snapshot"}
                        </p>
                        {/* Caso especial: prorrogação tem só { from, to } e fica
                            mais legível como uma frase humana do que tabela. */}
                        {action === "EXTEND_END_DATE" && payload.target.from && payload.target.to ? (
                          <p className="text-xs text-foreground/80">
                            Data de término alterada de{" "}
                            <strong>{formatValue(payload.target.from)}</strong>{" "}
                            para{" "}
                            <strong>{formatValue(payload.target.to)}</strong>.
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {Object.entries(payload.target).map(([k, v]) => (
                              <div
                                key={k}
                                className="text-xs grid grid-cols-[140px_1fr] gap-2 py-0.5"
                              >
                                <span className="font-medium text-foreground/80 truncate">
                                  {fieldLabel(k)}
                                </span>
                                <span className="font-mono text-foreground/70 break-all">
                                  {formatValue(v)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {payload.metadata?.identifierRequest && (
                      <p className="text-[10px] font-mono text-muted-foreground/60 pt-1">
                        trace: {payload.metadata.identifierRequest}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
